import React, { FC, useMemo } from "react";
import { Image, ScrollView, useDevice, View } from "@lookiero/aurora";
import { VariantWithColorAndImage } from "../../../../../../domain/projections/VariantWithColorAndImage";
import { useGetVariantColors } from "../../../../behaviors/useGetVariantColors";
import { TouchableWithoutFeedback } from "../../../../components/touchableWithoutFeedback/TouchableWithoutFeedback";
import { cdnImageUrl } from "../../../../shared/util/cdnImageUrl";
import { style as scrollableColorPickerStyle, imageWidth } from "./ScrollableColorPicker.styles";

interface ScrollableColorPickerProps {
  readonly variantId: string;
  readonly color: string;
  readonly onPressColor: (color: VariantWithColorAndImage) => void;
}

const ScrollableColorPicker: FC<ScrollableColorPickerProps> = ({ color, variantId, onPressColor }) => {
  const { variantColors } = useGetVariantColors(variantId);
  const { screen } = useDevice();

  const style = useMemo(() => scrollableColorPickerStyle(), []);

  const colorVariants = useMemo(() => {
    if (!variantColors) {
      return [];
    }
    const variantsPerColor = variantColors.reduce(
      (acc, colorVariant) => {
        const colorId = colorVariant.color.id;
        if (!acc[colorId] && colorVariant.isSizeRecommended && colorVariant.isSizeAvailable) {
          acc[colorId] = colorVariant;
        }
        return acc;
      },
      {} as Record<string, VariantWithColorAndImage>,
    );

    return Object.values(variantsPerColor);
  }, [variantColors]);

  const selectedColor = useMemo(() => {
    if (color && colorVariants.length > 0) {
      const colorVariant = colorVariants.find((variant) => variant.color.name === color);
      if (colorVariant) {
        return colorVariant.id;
      }
    }
    return "";
  }, [colorVariants, color]);

  return colorVariants.length ? (
    <View style={screen.L ? style.wrapper : null}>
      <ScrollView contentContainerStyle={style.content} testID="color-picker" horizontal>
        {colorVariants.map((colorVariant) => {
          return (
            <TouchableWithoutFeedback
              key={colorVariant.id}
              style={style.imageWrapper}
              onPress={() => onPressColor(colorVariant)}
            >
              <Image
                resizeMode="cover"
                src={cdnImageUrl({ imageUrl: colorVariant.url, width: imageWidth })}
                style={[style.image, colorVariant.id === selectedColor ? style.selectedImage : null]}
              />
            </TouchableWithoutFeedback>
          );
        })}
      </ScrollView>
    </View>
  ) : null;
};

export { ScrollableColorPicker };
